import UserDriverListResponse, {
  UserDriverListResponseItemDriverCarOwnership,
  UserDriverListResponseItemDriver,
  UserDriverListResponseItemAddressType,
  UserDriverListResponseItemAddress,
  UserDriverListResponseItemTaxiAssociationFleetPartner,
  UserDriverListResponseItemTaxiDriverAssociationTaxiCorporation,
  UserDriverListResponseItemTaxiDriverAssociation,
  UserDriverListResponseItem,
} from "../../../../api/raily/user/driver/list/user-driver-list.response";
import AddressType from "../../../../types/address-type";
import UserVehicleOwnership from "../../../../types/user-vehicle-ownership-form";
import UserDriverList, {
  UserDriverListItem,
  UserDriverListItemVehicle,
  UserDriverListItemTaxiAssociationFleetPartner,
  UserDriverListItemTaxiAssociation,
  UserDriverListItemTaxiAssociationTaxiCorporation,
  UserDriverListItemAddress,
} from "./user-driver-list";

const createDriverCarOwnership = (
  responseDriverCarOwnership: UserDriverListResponseItemDriverCarOwnership,
): UserVehicleOwnership => {
  switch (responseDriverCarOwnership) {
    case UserDriverListResponseItemDriverCarOwnership.BUSINESS:
      return UserVehicleOwnership.BUSINESS;
    case UserDriverListResponseItemDriverCarOwnership.OWNER:
      return UserVehicleOwnership.OWNER;
  }
};

const createVehicle = (
  responseDriver: UserDriverListResponseItemDriver,
): UserDriverListItemVehicle => {
  return {
    make: responseDriver.carMake,
    model: responseDriver.carModel,
    ownership: createDriverCarOwnership(responseDriver.carOwnership),
    productionYear: responseDriver.carProdYear,
  };
};

const createDriverAddressType = (
  responseDriverAddressType: UserDriverListResponseItemAddressType,
): AddressType => {
  switch (responseDriverAddressType) {
    case UserDriverListResponseItemAddressType.MAILING:
      return AddressType.MAILING;
    case UserDriverListResponseItemAddressType.STARTING:
      return AddressType.STARTING;
  }
};

const createDriverAddress = (
  responseDriverAddress: UserDriverListResponseItemAddress,
): UserDriverListItemAddress => {
  return {
    town: responseDriverAddress.town,
    type: createDriverAddressType(responseDriverAddress.type),
    zipCode: responseDriverAddress.zipCode,
  };
};

const createAddresses = (
  responseDriverAddresses: UserDriverListResponseItemAddress[],
): UserDriverListItemAddress[] => {
  return responseDriverAddresses.map(createDriverAddress);
};

const createDriverTaxiAssociationFleetPartner = (
  responseDriverTaxiAssociationFleetPartner: UserDriverListResponseItemTaxiAssociationFleetPartner | null,
): UserDriverListItemTaxiAssociationFleetPartner | null => {
  if (!responseDriverTaxiAssociationFleetPartner) {
    return null;
  }

  return {
    name: responseDriverTaxiAssociationFleetPartner.name,
  };
};
const createDriverTaxiAssociationTaxiCorporation = (
  responseDriverTaxiAssociationTaxiCorporation: UserDriverListResponseItemTaxiDriverAssociationTaxiCorporation,
): UserDriverListItemTaxiAssociationTaxiCorporation => {
  return {
    displayName: responseDriverTaxiAssociationTaxiCorporation.displayName,
  };
};

const createDriverTaxiAssociation = (
  responseDriverTaxiAssociation: UserDriverListResponseItemTaxiDriverAssociation,
): UserDriverListItemTaxiAssociation => {
  return {
    fleetPartner: createDriverTaxiAssociationFleetPartner(
      responseDriverTaxiAssociation.fleetPartner,
    ),
    taxiCorporation: createDriverTaxiAssociationTaxiCorporation(
      responseDriverTaxiAssociation.taxiCorporation,
    ),
  };
};

const createTaxiAssociations = (
  responseDriverTaxiAssociations: UserDriverListResponseItemTaxiDriverAssociation[],
): UserDriverListItemTaxiAssociation[] => {
  return responseDriverTaxiAssociations.map(createDriverTaxiAssociation);
};

const createAlternativePhoneNumber = (
  responseDataItem: UserDriverListResponseItem,
): UserDriverListItem["alternativePhoneNumber"] => {
  if (
    !responseDataItem.mobileSecondary ||
    !responseDataItem.mobileSecondaryPrefix
  ) {
    return null;
  }

  return {
    dialingCode: responseDataItem.mobileSecondaryPrefix,
    number: responseDataItem.mobileSecondary,
  };
};

const createPhoneNumber = (
  responseDataItem: UserDriverListResponseItem,
): UserDriverListItem["phoneNumber"] => {
  return {
    dialingCode: responseDataItem.mobilePrimaryPrefix,
    number: responseDataItem.mobilePrimary,
  };
};

const createDataItem = (
  responseDataItem: UserDriverListResponseItem,
): UserDriverListItem => {
  return {
    creationDate: responseDataItem.createdAt,
    modificationDate: responseDataItem.modifiedAt,
    createdByName: responseDataItem.createdByName,
    modifiedByName: responseDataItem.modifiedByName,
    firstName: responseDataItem.firstName,
    isCandidaturePending: responseDataItem.pendingCandidature,
    lastName: responseDataItem.lastName,
    phoneNumber: createPhoneNumber(responseDataItem),
    alternativePhoneNumber: createAlternativePhoneNumber(responseDataItem),
    removalDate: responseDataItem.removedAt,
    addresses: createAddresses(responseDataItem.driver.addresses),
    vehicle: createVehicle(responseDataItem.driver),
    taxiAssociations: createTaxiAssociations(
      responseDataItem.driver.taxiDriverAssociations,
    ),
    uuid: responseDataItem.id,
    lastPlanEntryEndTime: responseDataItem.driver.lastPlanEntryEndTime,
  };
};

const createData = (
  responseData: UserDriverListResponseItem[],
): UserDriverListItem[] => {
  return responseData.map(createDataItem);
};

const create = (response: UserDriverListResponse): UserDriverList => {
  return {
    data: createData(response.data.data),
    totalCount: response.data.totalCount,
  };
};

const userDriverListFactory = {
  create,
};

export default userDriverListFactory;
